import { useCompanyLabels } from '../context/CompanyContext';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../components/atoms/Button';
import Loading from '../components/atoms/Loading';
import { getFormFields } from '../redux/complaint/actions';
import { createComplaint } from '../redux/createComplaint/actions';
import { isStatusField, toUiFieldType } from '../data/formFieldPresets';
import { useDispatch } from 'react-redux';
import './querymanagement.css';
import '../components/organisms/settings-steps/review.css';
import StoreSelect from '../components/molecules/StoreSelect';
import { isStoreIdField } from '../data/formFieldPresets';

const DEFAULT_COMPLAINT_TYPE_ID = 37;

const FIELD_TYPE_MAP = {
	CUSTOM: 'Custom',
	TEXT: 'Text',
	TEXTAREA: 'Text Area',
	SELECT: 'Select',
	DROPDOWN: 'Dropdown',
	NUMBER: 'Number',
	DATE: 'Date',
	DATETIME: 'Date Time',
	EMAIL: 'Email',
	// MULTISELECT: 'MultiSelect',
	// MULTI_SELECT: 'MultiSelect',
	CHECKBOX: 'Checkbox',
	RADIO: 'Radio',
	// MOBILE: 'Mobile',
	// PHONE: 'Mobile',
	FILE: 'File',
	IMAGES: 'Images',
	TIME: 'Time',
};

function optionLabel(value) {
	if (typeof value === 'string') return value;
	return value?.label ?? '';
}

function normalizeFormFields(list = []) {
	const sorted = [...list].sort(
		(a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
	);

	return sorted.map((item) => {
		const label = item.fieldName ?? item.fieldLabel ?? item.label ?? '';
		const rawKey = item.fieldKey ?? String(item.id);
		return {
			id: item.id,
			label,
			fieldKey: isStatusField({ fieldKey: rawKey, label }) ? 'status' : rawKey,
			fieldType: toUiFieldType(item.fieldType, FIELD_TYPE_MAP, item.fieldKey),
			required: Boolean(item.required),
			options: (item?.options?.values ?? []).map(optionLabel).filter(Boolean),
		};
	});
}

function toFieldKey(label) {
	const words = String(label || '')
		.trim()
		.split(/[^A-Za-z0-9]+/)
		.filter(Boolean);
	if (!words.length) return '';
	return (
		words[0].toLowerCase() +
		words
			.slice(1)
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join('')
	);
}

function apiFieldKey(field) {
	if (isStatusField(field)) return 'status';
	const key = String(field.fieldKey || '').trim();
	if (key && !/\s/.test(key)) return key;
	return toFieldKey(field.label || key);
}

function fieldName(field) {
	return String(field.fieldKey || field.id || field.label);
}

function fieldType(field) {
	const type = String(field.fieldType || '').toLowerCase();
	if (type === 'textarea' || type === 'text area') return 'textarea';
	if (type === 'date') return 'date';
	if (type === 'date time' || type === 'datetime' || type === 'datetime-local') return 'datetime-local';
	if (type === 'time') return 'time';
	if (type === 'email') return 'email';
	if (type === 'number' || type === 'mobile') return 'number';
	if (type === 'radio') return 'radio';
	if (type === 'checkbox' || type === 'multiselect') return 'checkbox';
	// if (type === 'mobile') return 'mobile';
	if (type === 'file') return 'file';
	if (type === 'images') return 'images';
	if (type === 'dropdown' || type === 'select' || type === 'custom') {
		return field.options.length ? 'select' : 'text';
	}
	return 'text';
}

function questionId(field) {
	return `new-query-${field.id ?? fieldName(field)}`;
}

function initialValue(field) {
	if (isStatusField(field)) {
		const open = (field.options || []).find((item) => String(item).trim().toLowerCase() === 'open');
		return open || 'Open';
	}
	const type = fieldType(field);
	if (type === 'checkbox' || type === 'images') return [];
	return '';
}

function fieldValue(value) {
	if (Array.isArray(value)) {
		return value.map((item) => (
			typeof File !== 'undefined' && item instanceof File ? item.name : item
		));
	}
	if (typeof File !== 'undefined' && value instanceof File) return value.name;
	if (value == null) return '';
	return value;
}

function isEmptyValue(value) {
	if (Array.isArray(value)) return value.length === 0;
	if (typeof File !== 'undefined' && value instanceof File) return false;
	return value == null || String(value).trim() === '';
}

function isMobileValue(value) {
	return /^\d+$/.test(String(value).trim());
}

function validateFieldValues(fields = [], values = {}) {
	for (const field of fields) {
		const type = fieldType(field);
		const value = values[fieldName(field)];
		const label = field.label || 'This field';
		if (field.required && isEmptyValue(value)) return `${label} is required.`;
		if (isEmptyValue(value)) continue;
		if (type === 'number' && !isMobileValue(value)) return `${label} must be a valid number.`;
		// if (type === 'mobile' && !isMobileValue(value)) return `${label} must be a valid mobile number.`;
	}
	return '';
}

function buildFieldValues(fields = [], values = {}) {
	const fieldValues = {};
	fields.forEach((field) => {
		const key = apiFieldKey(field);
		if (!key) return;
		fieldValues[key] = fieldValue(values[fieldName(field)]);
	});
	return fieldValues;
}

function buildTitle(fields = [], values = {}, fieldValues = {}) {
	const titleField = fields.find((field) => {
		const label = String(field.label || '').trim().toLowerCase();
		const key = String(field.fieldKey || '').trim().toLowerCase();
		return label === 'title' || key === 'title';
	});
	if (titleField) {
		const value = values[fieldName(titleField)];
		if (value) return String(value);
	}
	if (values.title) return String(values.title);
	const category = fieldValues.complaintCategory || fieldValues.status || fieldValues['Complaint Category'] || fieldValues.Status;
	if (category) return String(category);
	return 'Untitled Query';
}

function NewQueryControl({ field, value, onChange, storeList }) {
  const { fieldLabel } = useCompanyLabels();
	const type = fieldType(field);
	const id = questionId(field);
	const locked = isStatusField(field);

	if (isStoreIdField(field) && Array.isArray(storeList)) {
		return <StoreSelect id={id} name={fieldName(field)} required={field.required} storeList={storeList} value={value} onChange={onChange} />;
	}

	if (type === 'radio') {
		return (
			<div className="review-options review-options--inline">
				{field.options.map((option) => (
					<label className="review-option" htmlFor={`${id}-${option}`} key={option}>
						<input
							id={`${id}-${option}`}
							name={fieldName(field)}
							type="radio"
							value={option}
							checked={value === option}
							onChange={(event) => onChange(event.target.value)}
							required={field.required}
							disabled={locked}
						/>
						<span>{option}</span>
					</label>
				))}
			</div>
		);
	}

	if (type === 'checkbox') {
		const selected = Array.isArray(value) ? value : [];
		return (
			<div className="review-options review-options--inline">
				{field.options.map((option) => (
					<label className="review-option" htmlFor={`${id}-${option}`} key={option}>
						<input
							id={`${id}-${option}`}
							name={`${fieldName(field)}[]`}
							type="checkbox"
							value={option}
							checked={selected.includes(option)}
							onChange={(event) => onChange(
								event.target.checked
									? [...selected, option]
									: selected.filter((item) => item !== option),
							)}
						/>
						<span>{option}</span>
					</label>
				))}
			</div>
		);
	}

	if (type === 'textarea') {
		return <textarea id={id} name={fieldName(field)} className="review-control review-control--textarea" value={value} onChange={(event) => onChange(event.target.value)} required={field.required} disabled={locked} />;
	}

	if (type === 'file') {
		return (
			<input
				id={id}
				name={fieldName(field)}
				className="review-control"
				type="file"
				onChange={(event) => onChange(event.target.files?.[0] || '')}
				required={field.required}
				disabled={locked}
			/>
		);
	}

	if (type === 'images') {
		return (
			<input
				id={id}
				name={fieldName(field)}
				className="review-control"
				type="file"
				accept="image/*"
				multiple
				onChange={(event) => onChange(Array.from(event.target.files || []))}
				required={field.required}
				disabled={locked}
			/>
		);
	}

	if (type === 'number') {
		return (
			<input
				id={id}
				name={fieldName(field)}
				className="review-control"
				type="number"
				inputMode="numeric"
				value={value}
				onChange={(event) => onChange(String(event.target.value).replace(/[^\d]/g, ''))}
				onKeyDown={(event) => {
					if (event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '-' || event.key === '.') {
						event.preventDefault();
					}
				}}
				required={field.required}
				disabled={locked}
			/>
		);
	}

	// if (type === 'mobile') {
	// 	return (
	// 		<input
	// 			id={id}
	// 			name={fieldName(field)}
	// 			className="review-control"
	// 			type="number"
	// 			inputMode="numeric"
	// 			value={value}
	// 			onChange={(event) => onChange(String(event.target.value).replace(/[^\d]/g, ''))}
	// 			onKeyDown={(event) => {
	// 				if (event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '-' || event.key === '.') {
	// 					event.preventDefault();
	// 				}
	// 			}}
	// 			required={field.required}
	// 			disabled={locked}
	// 		/>
	// 	);
	// }

	if (type === 'select') {
		const options = (field.options || []).includes(value) || !locked || !value
			? field.options
			: [value, ...field.options];
		return (
			<select id={id} name={fieldName(field)} className="review-control" value={value} onChange={(event) => onChange(event.target.value)} required={field.required} disabled={locked}>
				{locked ? null : <option value="">Select {fieldLabel(field)}</option>}
				{options.map((option) => <option key={option} value={option}>{option}</option>)}
			</select>
		);
	}

	return <input id={id} name={fieldName(field)} className="review-control" type={type} value={value} onChange={(event) => onChange(event.target.value)} required={field.required} disabled={locked} />;
}

export default function NewQuery({ storeList } = {}) {
  const { fieldLabel } = useCompanyLabels();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { complaintTypeId } = useParams();
    // const complaintTypeId = 37; // Hardcoded complaintTypeId for testing purposes
	const complaintId = Number(complaintTypeId) || DEFAULT_COMPLAINT_TYPE_ID;
	const [fields, setFields] = useState([]);
	const [values, setValues] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');

	useEffect(() => {
		let cancelled = false;
		// setIsLoading(true);
		dispatch(getFormFields(complaintId))
			.unwrap()
			.then((response) => {
				if (cancelled) return;
				const nextFields = normalizeFormFields(Array.isArray(response?.data) ? response.data : response);
				setFields(nextFields);
				setValues(Object.fromEntries(nextFields.map((field) => [fieldName(field), initialValue(field)])));
			})
			.catch((requestError) => {
				if (!cancelled) setError(requestError?.message || 'Failed to load form fields.');
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false);
			});
		return () => { cancelled = true; };
	}, [complaintId, dispatch]);

	const hasFields = useMemo(() => fields.length > 0, [fields]);

	const handleSubmit = async (event) => {
		event.preventDefault();
		setError('');
		setSuccess('');
		setIsSaving(true);
		const validationError = validateFieldValues(fields, values);
		if (validationError) {
			setError(validationError);
			setIsSaving(false);
			return;
		}
		const fieldValues = buildFieldValues(fields, values);
		const payload = {
			complaintTypeId: complaintId,
			title: buildTitle(fields, values, fieldValues),
			fieldValues,
		};
		console.log('Submitting query with values:', payload);
		try {
			await dispatch(createComplaint(payload)).unwrap();
			setSuccess('Query submitted successfully.');
			navigate(`/querymanagement/${complaintId}`);
		} catch (requestError) {
			setError(requestError?.message || 'Failed to submit query.');
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<main className="ca-query-management-page">
			<section className="ca-query-details" aria-labelledby="new-query-title">
				<div className="ca-query-details__heading">
					<div className="ca-query-details__title-row">
						<button
							type="button"
							className="ca-settings-page__back"
							onClick={() => navigate(`/querymanagement/${complaintId}`)}
							aria-label="Back to Complaint Management"
						>
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
								<path
									d="M15 6l-6 6 6 6"
									stroke="currentColor"
									strokeWidth="2.2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
						<h1 id="new-query-title" className="ca-query-details__title">New Complaint</h1>
					</div>
				</div>
				{error ? (
					<div className="ca-validation-toast" role="alert">{error}</div>
				) : null}
				{isLoading ? <Loading text="Loading form fields" /> : null}
				{!isLoading && hasFields ? (
					<form className="review-form-card" onSubmit={handleSubmit}>
						<div className="review-form-grid">
							{fields.map((field) => (
								<div className={`review-question${['textarea', 'radio', 'checkbox'].includes(fieldType(field)) ? ' review-question--wide' : ''}`} key={field.id ?? fieldName(field)}>
									<label className="review-label" htmlFor={questionId(field)}>
										{fieldLabel(field) || 'Untitled field'}
										{field.required ? <span className="review-required">*</span> : null}
									</label>
									<NewQueryControl storeList={storeList}
										field={field}
										value={values[fieldName(field)] ?? initialValue(field)}
										onChange={(value) => {
											if (isStatusField(field)) return;
											setValues((current) => ({ ...current, [fieldName(field)]: value }));
										}}
									/>
								</div>
							))}
						</div>
						<div className="ca-new-query__actions">
							<Button type="submit" disabled={isSaving}>{isSaving ? 'Submitting...' : 'Submit Complaint'}</Button>
						</div>
						{success ? <p className="ca-body">{success}</p> : null}
					</form>
				) : null}
			</section>
		</main>
	);
}
